/**
 * HeroAnimatedSlide — the floating preview card that overlays the hero photo.
 *
 * Loops through 4 phases on a ~10s timer so visitors actually see SlideCon's
 * AI flow without leaving the landing page:
 *   1. INPUT      — user "types" a Bible verse reference
 *   2. OUTLINE    — outline points materialize one by one
 *   3. BUILDING   — shimmer + "Building slides…" caption
 *   4. SLIDE      — final worship slide
 *
 * Pure CSS + framer-motion. No external Lottie. Respects prefers-reduced-motion.
 */
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Music, Sparkles, Loader2, BookOpen, ArrowRight } from "lucide-react";

const PHASES = ["input", "outline", "building", "slide"];
const PHASE_DURATIONS = { input: 2800, outline: 3200, building: 1600, slide: 3200 };

function usePhase() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const phase = PHASES[idx];
    const t = setTimeout(() => setIdx((i) => (i + 1) % PHASES.length), PHASE_DURATIONS[phase]);
    return () => clearTimeout(t);
  }, [idx]);
  return PHASES[idx];
}

// Typewriter that appears as the input phase animates in.
function TypingVerse() {
  const text = "Romans 8:28";
  const [shown, setShown] = useState("");
  useEffect(() => {
    let i = 0;
    setShown("");
    const id = setInterval(() => {
      i += 1;
      setShown(text.slice(0, i));
      if (i >= text.length) clearInterval(id);
    }, 110);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="font-mono text-sm text-stone-800">
      {shown}
      <span className="inline-block w-[1px] h-[14px] bg-stone-700 align-middle ml-0.5 animate-pulse" />
    </span>
  );
}

const outlineRows = [
  { label: "1. God works through every storm", delay: 0.05 },
  { label: "2. Called for His purpose", delay: 0.4 },
  { label: "3. Hope, not just optimism", delay: 0.75 },
];

export default function HeroAnimatedSlide() {
  const phase = usePhase();

  return (
    <div
      className="lp-card relative w-72 sm:w-80 h-72 p-5 overflow-hidden"
      role="img"
      aria-label="A four-phase preview showing how SlideCon turns a Bible verse into a finished worship slide."
      data-testid="hero-animated-preview"
    >
      <AnimatePresence mode="wait">
        {phase === "input" && (
          <motion.div
            key="input"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
            className="h-full flex flex-col"
          >
            <div className="lp-pill mb-3">
              <BookOpen className="w-3 h-3" /> Try a verse
            </div>
            <p className="text-xs text-stone-500 mb-2">Paste a Bible reference</p>
            <div className="rounded-lg border border-stone-200 bg-white px-3 py-2.5 mb-3">
              <TypingVerse />
            </div>
            <div className="mt-auto flex items-center gap-2 text-xs text-stone-500">
              <ArrowRight className="w-3.5 h-3.5" />
              <span>SlideCon drafts the outline next</span>
            </div>
          </motion.div>
        )}

        {phase === "outline" && (
          <motion.div
            key="outline"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
            className="h-full flex flex-col"
          >
            <div className="lp-pill mb-3">
              <Sparkles className="w-3 h-3" /> Drafting outline
            </div>
            <p className="font-display text-base font-semibold text-stone-900 mb-2 leading-tight">
              More Than Conquerors
            </p>
            <p className="text-[11px] text-stone-500 mb-2">Romans 8:28 · 3-point outline</p>
            <ul className="space-y-1.5">
              {outlineRows.map((row) => (
                <motion.li
                  key={row.label}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: row.delay }}
                  className="text-xs text-stone-700 leading-snug border-l-2 border-[#C47053]/60 pl-2"
                >
                  {row.label}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}

        {phase === "building" && (
          <motion.div
            key="building"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="h-full flex flex-col items-center justify-center text-center gap-3"
          >
            <Loader2 className="w-7 h-7 text-[#C47053] animate-spin" />
            <p className="text-sm text-stone-700 font-medium">Building slides…</p>
            <div className="w-full space-y-1.5 mt-1">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-2 rounded bg-stone-200 overflow-hidden relative"
                >
                  <motion.div
                    initial={{ x: "-100%" }}
                    animate={{ x: "100%" }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: "linear",
                      delay: i * 0.18,
                    }}
                    className="absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-white/70 to-transparent"
                  />
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {phase === "slide" && (
          <motion.div
            key="slide"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.45 }}
            className="h-full flex flex-col"
          >
            <div className="lp-pill mb-3">
              <Music className="w-3 h-3" /> Worship slide
            </div>
            <p className="font-display text-2xl leading-snug text-stone-900">
              Amazing grace,
            </p>
            <p className="font-display text-xl italic text-stone-600 leading-snug">
              how sweet the sound
            </p>
            <div className="mt-auto pt-2 border-t border-stone-200 flex items-center justify-between text-[11px] text-stone-500">
              <span>Verse 1 · ready for Sunday</span>
              <span className="text-[#C47053] font-medium">8 slides</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Phase dots */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
        {PHASES.map((p) => (
          <span
            key={p}
            className={`h-1 rounded-full transition-all ${
              p === phase ? "w-6 bg-[#C47053]" : "w-1.5 bg-stone-300"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
